import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import {
  SKUItem,
  SKUDetail,
  SKUAttribute,
  Barcode,
} from '../../models/sku.interface';
import { SKUService } from '../../services/sku.service';
import { SKUMapperService } from '../../services/sku-mapper.service';
import {
  SKU_STATUSES,
  getSKUStatusByCode,
  GOODS_ATTRIBUTE_SOURCES_JSON,
  ATTRIBUTE_SECTION_LABELS,
  AttributeSourceType,
} from '../../models/sku.constants';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { BarcodeDialogComponent } from '../barcode-dialog/barcode-dialog.component';
import { BarcodeTableComponent } from '../barcode-table/barcode-table.component';
import { CheckboxModule } from 'primeng/checkbox';
import { SkuErrorService } from './skuErrorService';
import { ErrorDialogService } from '@shared/services/error-dialog.service';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';
import { SpuApiService } from '../../../spu/services/spu-api.service';
import { SkuPackageVO } from '@app/api/model/skuPackageVO';
import { TaxCategoryService } from '@app/feature/configuration/product/tax-category/services/tax-category.service';
import { ProductTaxControlComponent } from '../../../shared/product-tax/product-tax-control.component';
import {
  ProductTaxInheritedValue,
  ProductTaxOption,
  ProductTaxValue,
} from '../../../shared/product-tax/product-tax.model';
import {
  DEFAULT_PRODUCT_TAX_VALUE,
  deriveProductTaxMode,
  normalizeProductTaxSubmit,
} from '../../../shared/product-tax/product-tax.util';
import { SkuTaxResolutionService } from '../../../shared/product-tax/sku-tax-resolution.service';
import { PermissionService } from '@app/core/services/permission.service';
import {
  GoodsSkuControllerService,
  ProductFieldVO,
} from '@app/api/goods-service';

interface PackageSkuComponent {
  packageSkuId: number;
  skuCode: string;
  skuName: string;
  spuCode?: string;
  spuName?: string;
  num: number;
}

interface PackageSpuAttribute {
  name: string;
  value: string;
}

@Component({
  selector: 'app-sku-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    CardModule,
    DividerModule,
    InputTextModule,
    InputNumberModule,
    TagModule,
    TooltipModule,
    ToastModule,
    ConfirmDialogModule,
    CheckboxModule,
    AppButtonComponent,
    BarcodeDialogComponent,
    BarcodeTableComponent,
    ProductTaxControlComponent,
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './sku-edit.component.html',
  styleUrl: './sku-edit.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SKUEditComponent
  implements AfterViewInit, OnDestroy, CanComponentDeactivate
{
  readonly dimensionFractionDigits = 3;

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly skuService = inject(SKUService);
  private readonly spuApiService = inject(SpuApiService);
  private readonly mapperService = inject(SKUMapperService);
  private readonly fb = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translateService = inject(TranslateService);
  private readonly elementRef = inject(ElementRef);
  private readonly skuErrorService = inject(SkuErrorService);
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly taxCategoryService = inject(TaxCategoryService);
  private readonly skuTaxResolutionService = inject(SkuTaxResolutionService);
  private readonly permissionService = inject(PermissionService);
  private readonly goodsSkuControllerService = inject(GoodsSkuControllerService);
  private currentSpuId: number | null = null;
  private scrollContentEl: HTMLElement | null = null;
  private readonly scrollHandler = () => {
    this.isScrolled.set((this.scrollContentEl?.scrollTop ?? 0) > 10);
  };

  skuItem = signal<SKUItem | null>(null);
  loading = signal(false);
  private skipCanDeactivate = false;
  isScrolled = signal(false);
  editForm: FormGroup;

  // Barcode management
  barcodeList = signal<Barcode[]>([]);
  selectedBarcodes = signal<Barcode[]>([]);
  showBarcodeDialog = signal(false);
  editingBarcode = signal<Barcode | null>(null);
  nextBarcodeId = signal(1);
  packageComponents = signal<PackageSkuComponent[]>([]);
  selectedPackageComponents = signal<PackageSkuComponent[]>([]);
  packageSpuAttributes = signal<PackageSpuAttribute[]>([]);
  taxOptions = signal<ProductTaxOption[]>([]);
  taxValue = signal<ProductTaxValue>(DEFAULT_PRODUCT_TAX_VALUE);
  inheritedTaxValue = signal<ProductTaxInheritedValue | null>(null);
  taxResolutionReady = signal(false);

  // Field metadata for field-level permission gating (see PermissionService.canEditField)
  productFields = signal<ProductFieldVO[]>([]);
  canEditBarcode = signal(true);
  canEditPackageComposition = signal(true);

  constructor() {
    this.editForm = this.fb.group({
      skuCode: [''],
      weight: [0, [Validators.min(0)]],
      length: [0, [Validators.min(0)]],
      width: [0, [Validators.min(0)]],
      height: [0, [Validators.min(0)]],
    });

    this.loadTaxOptions();
    this.loadSkuItem();
    this.loadProductFields();
  }

  private loadTaxOptions(): void {
    this.taxCategoryService.getActiveOptions().subscribe({
      next: (options) => this.taxOptions.set(options),
      error: () => this.taxOptions.set([]),
    });
  }

  private loadProductFields(): void {
    this.goodsSkuControllerService
      .getMappingFields('body', false, { httpHeaderAccept: 'application/json' as any })
      .subscribe({
      next: (fields) => {
        this.productFields.set(fields);
        this.applyFieldPermissions();
      },
      error: () => {
        this.productFields.set([]);
        this.applyFieldPermissions();
      },
    });
  }

  private requiredPermissionFor(fieldCode: string): string | null | undefined {
    return this.productFields().find((field) => field.code === fieldCode)
      ?.requiredPermission;
  }

  canEditField(requiredPermission: string | null | undefined): boolean {
    return this.permissionService.canEditField(requiredPermission);
  }

  private applyFieldPermissions(): void {
    this.canEditBarcode.set(
      this.canEditField(this.requiredPermissionFor('barcode')),
    );
    this.canEditPackageComposition.set(
      this.canEditField(this.requiredPermissionFor('package_composition')),
    );
  }

  ngAfterViewInit(): void {
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      this.setupScrollListener();
    }, 100);
  }

  @HostListener('window:resize')
  onResize(): void {
    this.setupScrollListener();
  }

  private setupScrollListener(): void {
    const contentElement =
      this.elementRef.nativeElement.querySelector('.sku-edit-content');
    if (contentElement) {
      if (this.scrollContentEl) {
        this.scrollContentEl.removeEventListener('scroll', this.scrollHandler);
      }
      this.scrollContentEl = contentElement;
      contentElement.addEventListener('scroll', this.scrollHandler);
    }
  }

  private currentSkuType(): 'ordinary' | 'package' {
    return this.route.snapshot.paramMap.get('type') === 'package'
      ? 'package'
      : 'ordinary';
  }

  ngOnDestroy(): void {
    this.scrollContentEl?.removeEventListener('scroll', this.scrollHandler);
  }

  private loadSkuItem(): void {
    this.loading.set(true);
    const skuId = this.route.snapshot.paramMap.get('id');
    if (skuId) {
      this.skuService.getSkuDetailById(skuId).subscribe({
        next: (skuDetail) => {
          if (skuDetail) {
            const skuItem = this.mapperService.mapDetailToItem(skuDetail);
            this.currentSpuId = Number(skuDetail.spuId) || null;
            this.skuItem.set(skuItem);
            this.initializeForm(skuItem);
            this.resolveSkuTax(skuItem);
            if (skuDetail.setFlag === 1) {
              this.loadPackageSpuAttributes(Number(skuDetail.spuId));
            } else {
              this.packageSpuAttributes.set([]);
            }
          }
          this.loading.set(false);
        },
        error: (error) => {
          this.loading.set(false);
        },
      });
    }
  }

  private initializeForm(item: SKUItem): void {
    this.editForm.patchValue({
      skuCode: item.skuCode,
      weight: item.productWeight || 0,
      length: item.productLength || 0,
      width: item.productWidth || 0,
      height: item.productHeight || 0,
    });
    this.taxValue.set({
      mode: deriveProductTaxMode(item.taxId, item.isPriceIncludeVAT),
      taxId: item.taxId ?? null,
      isPriceIncludeVAT: item.isPriceIncludeVAT ?? null,
    });
    this.inheritedTaxValue.set({
      taxId: item.taxId ?? null,
      taxCode: item.taxCode ?? null,
      taxRate: null,
      isPriceIncludeVAT: item.isPriceIncludeVAT ?? null,
      source:
        deriveProductTaxMode(item.taxId, item.isPriceIncludeVAT) === 'OVERRIDE'
          ? 'SKU_OVERRIDE'
          : 'UNCONFIGURED',
    });
    this.configureSkuCodeValidation();

    // Initialize barcode list from API response
    const existingBarcodes = item.barcodes || (item as any).barcodeList;

    // Debug: Log existing barcodes
    if (
      existingBarcodes &&
      Array.isArray(existingBarcodes) &&
      existingBarcodes.length > 0
    ) {
      // Map API response format to our Barcode interface
      // API returns: { id, barcodeValue, barcodeType, skuId, ... }
      const mappedBarcodes = existingBarcodes.map((b: any) => ({
        id: b.id || this.nextBarcodeId(),
        barcodeValue: b.barcodeValue || b.barcode || '',
        barcodeType: (b.barcodeType || 'EAN') as Barcode['barcodeType'],
      }));
      this.barcodeList.set(mappedBarcodes);
      // Update nextBarcodeId to avoid conflicts
      const maxId = Math.max(
        ...mappedBarcodes.map((b: Barcode) => b.id || 0),
        0,
      );
      if (maxId > 0) {
        this.nextBarcodeId.set(maxId + 1);
      } else {
        this.nextBarcodeId.update((id) => id + mappedBarcodes.length);
      }
    } else {
      this.barcodeList.set([]);
    }

    this.packageComponents.set(
      this.mapPackageComponents(item.skuDetailList || []),
    );
    this.selectedPackageComponents.set([]);

    // Add dynamic form controls for attributes
    this.addAttributeFormControls(item);
  }

  private resolveSkuTax(item: SKUItem): void {
    this.taxResolutionReady.set(false);
    this.skuTaxResolutionService.resolve(item.skuCode).subscribe({
      next: (resolution) => {
        const configuredVat = item.isPriceIncludeVAT ?? null;
        this.taxValue.set({
          mode: deriveProductTaxMode(resolution.configuredTaxId, configuredVat),
          taxId: resolution.configuredTaxId,
          isPriceIncludeVAT: configuredVat,
        });
        this.inheritedTaxValue.set(resolution.effective);
        this.taxResolutionReady.set(true);
      },
      error: () => {
        this.taxResolutionReady.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'goods.tax.messages.resolution_failed',
          ),
          life: 5000,
        });
      },
    });
  }

  /**
   * SKU code is displayed as read-only for most statuses. Existing SKU codes
   * can exceed the edit-time 32 character limit, so they must not invalidate
   * unrelated updates such as adding a barcode.
   */
  private configureSkuCodeValidation(): void {
    const skuCodeControl = this.editForm.get('skuCode');
    if (!skuCodeControl) {
      return;
    }

    if (this.isSkuCodeEditable()) {
      skuCodeControl.setValidators([
        Validators.required,
        Validators.maxLength(32),
        this.skuCodeValidator,
      ]);
    } else {
      skuCodeControl.clearValidators();
    }

    skuCodeControl.updateValueAndValidity({ emitEvent: false });
  }

  private loadPackageSpuAttributes(spuId: number): void {
    if (!spuId) {
      this.packageSpuAttributes.set([]);
      return;
    }

    this.spuApiService.getSpuById(spuId).subscribe({
      next: (spu) => {
        const attributes = (spu?.attrLists || [])
          .filter((attribute) => !attribute.isSku)
          .map((attribute) => ({
            name: attribute.attributeName,
            value: (attribute.values || [])
              .map((value) => value.value)
              .filter((value): value is string => Boolean(value))
              .join(', '),
          }))
          .filter((attribute) => attribute.name && attribute.value);

        this.packageSpuAttributes.set(attributes);
      },
      error: () => {
        this.packageSpuAttributes.set([]);
      },
    });
  }

  private mapPackageComponents(
    components: SkuPackageVO[],
  ): PackageSkuComponent[] {
    return components
      .map((component) => ({
        packageSkuId: Number(component.packageSkuId || 0),
        skuCode: component.skuCode || '',
        skuName: component.skuName || '',
        spuCode: component.spuCode,
        spuName: component.spuName,
        num: Math.max(1, Number(component.num || 1)),
      }))
      .filter((component) => component.packageSkuId > 0);
  }

  private addAttributeFormControls(item: SKUItem): void {
    if (item.skuAttributeList) {
      item.skuAttributeList.forEach((attribute) => {
        if (this.isAttributeEditable(attribute.attributeType)) {
          const controlName = `attribute_${attribute.id}`;
          this.editForm.addControl(
            controlName,
            this.fb.control(attribute.attributeValue || ''),
          );
        }
      });
    }
  }

  onBack(): void {
    this.router.navigate(['/goods/sku', this.currentSkuType()]);
  }

  onSave(): void {
    // Mark all fields as touched to show validation errors
    this.editForm.markAllAsTouched();

    if (!this.taxResolutionReady()) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'goods.tax.messages.resolution_failed',
        ),
        life: 5000,
      });
      return;
    }

    if (!this.editForm.valid) {
      // Show validation error message
      const invalidFields = Object.keys(this.editForm.controls).filter(
        (key) => this.editForm.get(key)?.invalid,
      );
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail:
          this.translateService.instant(
            'sku_management.edit.messages.validation_error',
          ) || 'Please fix validation errors before saving.',
        life: 5000,
      });
      return;
    }

    if (!this.skuItem()) {
      return;
    }

    const formValue = this.editForm.value;
    const item = this.skuItem()!;

    // Update the SKU item with form values - map to API expected structure
    const updateData: any = {
      skuName: this.skuItem()!.skuName,
      skuCode: formValue.skuCode,
      // Map barcode list to API format: { barcodeType, barcodeValue }
      // Always send array (even if empty) to allow clearing all barcodes
      barcodes: this.barcodeList().map((b) => ({
        barcodeType: b.barcodeType,
        barcodeValue: b.barcodeValue,
      })),
      weight: formValue.weight,
      length: formValue.length,
      width: formValue.width,
      height: formValue.height,
      setFlag: (item as any).setFlag,
      ...normalizeProductTaxSubmit(this.taxValue()),
    };

    if (item.skuType === 'package') {
      updateData.skuDetailList = this.packageComponents().map((component) => ({
        packageSkuId: component.packageSkuId,
        num: component.num,
      }));
    }

    // Add attribute updates - properly structure the attribute list
    if (item.skuAttributeList) {
      const updatedAttributes = item.skuAttributeList.map((attribute) => {
        if (this.isAttributeEditable(attribute.attributeType)) {
          const controlName = `attribute_${attribute.id}`;
          const newValue = formValue[controlName];
          if (newValue !== undefined) {
            return {
              ...attribute,
              attributeValue: newValue,
            };
          }
        }
        return attribute;
      });

      updateData.skuAttributeList = updatedAttributes;
    }

    this.loading.set(true);
    this.skuService.updateSkuItem(item.id.toString(), updateData).subscribe({
      next: (response) => {
        this.loading.set(false); // Reset loading state
        if (response.success) {
          this.skipCanDeactivate = true;
          this.editForm.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.actions.success'),
            detail: this.translateService.instant(
              'sku_management.edit.messages.update_success',
            ),
          });
          // Add a small delay to allow the toast to be displayed before navigation
          setTimeout(() => {
            // Get the current query parameters from the route
            const currentQueryParams = this.route.snapshot.queryParams;

            // Remove the 'id' parameter when navigating back to list page
            const { id, ...filterParams } = currentQueryParams;

            // Navigate back to the list page with preserved filter parameters
            this.router.navigate(['/goods/sku', this.currentSkuType()], {
              queryParams: filterParams,
            });
          }, 1000);
        } else {
          this.errorDialogService.showError(
            this.translateService.instant(
              'sku_management.edit.messages.update_failed',
            ),
          );
        }
      },
      error: (error) => {
        this.loading.set(false);
        if (error?.error?.errorCode === 'FIELD_PERMISSION_DENIED') {
          this.errorDialogService.showError(
            this.translateService.instant(
              'sku_management.edit.messages.field_permission_denied',
              { fields: this.deniedFieldLabels(error) },
            ),
          );
          return;
        }
        if (error?.error?.errorCode === 'SPEC_EMBEDDED_IN_PRODUCT_TEXT') {
          this.errorDialogService.showError(
            this.translateService.instant(
              'sku_management.edit.messages.spec_embedded_in_product_text',
            ),
          );
          return;
        }
        this.errorDialogService.showError(
          this.skuErrorService.handleApiError(error, 'update').detail ?? '',
        );
      },
    });
  }

  private deniedFieldLabels(error: any): string {
    const deniedCodes: string[] = Array.isArray(error?.error?.data)
      ? error.error.data
      : [];
    return deniedCodes
      .map(
        (code) =>
          this.productFields().find((field) => field.code === code)?.label ??
          code,
      )
      .join(', ');
  }

  onCancel(): void {
    // Get the current query parameters from the route
    const currentQueryParams = this.route.snapshot.queryParams;

    // Remove the 'id' parameter when navigating back to list page
    const { id, ...filterParams } = currentQueryParams;

    // Navigate back to the list page with preserved filter parameters
    this.router.navigate(['/goods/sku', this.currentSkuType()], {
      queryParams: filterParams,
    });
  }

  private loadInheritedTaxFromSpu(spuId: number | null): void {
    if (!spuId || this.taxValue().mode === 'OVERRIDE') {
      return;
    }

    this.spuApiService.getSpuById(spuId).subscribe({
      next: (spu) => {
        if (this.taxValue().mode === 'OVERRIDE') {
          return;
        }

        const taxId = (spu as any)?.taxId ?? null;
        const isPriceIncludeVAT = (spu as any)?.isPriceIncludeVAT ?? null;
        this.inheritedTaxValue.set({
          taxId,
          taxCode: (spu as any)?.taxCode ?? null,
          taxRate: (spu as any)?.taxRate ?? null,
          isPriceIncludeVAT,
          source:
            deriveProductTaxMode(taxId, isPriceIncludeVAT) === 'OVERRIDE'
              ? 'SPU_OVERRIDE'
              : 'UNCONFIGURED',
        });
      },
      error: () => {
        this.inheritedTaxValue.set({
          taxId: null,
          taxCode: null,
          taxRate: null,
          isPriceIncludeVAT: null,
          source: 'UNCONFIGURED',
        });
      },
    });
  }

  onTaxValueChange(value: ProductTaxValue): void {
    this.taxValue.set(value);
    this.editForm.markAsDirty();
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.loading()) {
      return true;
    }

    return !this.editForm.dirty;
  }

  getStatusSeverity(status: number): 'warning' | 'success' | 'danger' | 'info' {
    const statusObj = getSKUStatusByCode(status);

    if (!statusObj) return 'warning';

    switch (statusObj.code) {
      case SKU_STATUSES.EFFECTIVE.code:
        return 'success';
      case SKU_STATUSES.REJECTED.code:
      case SKU_STATUSES.EFFECTIVE_REJECTED.code:
        return 'danger';
      case SKU_STATUSES.UNDER_REVIEW.code:
      case SKU_STATUSES.EFFECTIVE_EDITING.code:
        return 'warning';
      default:
        return 'warning';
    }
  }

  getStatusDisplayText(status: number): string {
    const statusObj = getSKUStatusByCode(status);
    return statusObj ? statusObj.label : 'Unknown';
  }

  isSkuCodeEditable(): boolean {
    const item = this.skuItem();
    return item ? parseInt(item.status) === SKU_STATUSES.REJECTED.code : false;
  }

  // Editing a Rejected, Effective, or Effective - Rejected SKU resubmits it for review on
  // save, so the button should read "Submit" rather than "Save" for those statuses.
  private readonly RESUBMIT_ON_SAVE_STATUS: number[] = [
    SKU_STATUSES.REJECTED.code,
    SKU_STATUSES.EFFECTIVE.code,
    SKU_STATUSES.EFFECTIVE_REJECTED.code,
  ];

  saveButtonLabelKey(): string {
    const item = this.skuItem();
    const statusNumber = item ? parseInt(item.status, 10) : NaN;
    return this.RESUBMIT_ON_SAVE_STATUS.includes(statusNumber)
      ? 'sku_management.actions.submit'
      : 'sku_management.edit.save';
  }

  // Attribute methods
  getAttributesByType(attributeType: AttributeSourceType): SKUAttribute[] {
    const item = this.skuItem();
    if (!item?.skuAttributeList) return [];

    return item.skuAttributeList.filter(
      (attr) => (attr.source ?? 0) === attributeType,
    );
  }

  hasAttributesForType(attributeType: AttributeSourceType): boolean {
    return this.getAttributesByType(attributeType).length > 0;
  }

  getAttributeSectionLabel(attributeType: AttributeSourceType): string {
    return ATTRIBUTE_SECTION_LABELS[attributeType];
  }

  getDynamicAttributeTypes(): AttributeSourceType[] {
    return [
      GOODS_ATTRIBUTE_SOURCES_JSON.SPU,
      GOODS_ATTRIBUTE_SOURCES_JSON.CLASSIFY,
      GOODS_ATTRIBUTE_SOURCES_JSON.ATTACHED,
    ];
  }

  isAttributeEditable(attributeType: number): boolean {
    // System Attributes (SYS: 1) and Additional Attributes (ATTACHED: 3) are editable
    // Product Attributes (SPU: 0) and Category Attributes (CLASSIFY: 2) are read-only
    return (
      attributeType === GOODS_ATTRIBUTE_SOURCES_JSON.SYS ||
      attributeType === GOODS_ATTRIBUTE_SOURCES_JSON.ATTACHED
    );
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    // Set a fallback image with a simple placeholder
    img.src =
      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxjaXJjbGUgY3g9IjIwMCIgY3k9IjE1MCIgcj0iNTAiIGZpbGw9IiM5Q0EzQUYiLz4KPHJlY3QgeD0iMTUwIiB5PSIyMDAiIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjOUNBM0FGIi8+Cjwvc3ZnPgo=';
    img.alt = 'Image not available';
    img.classList.add('opacity-50');
  }

  /**
   * Custom validator for SKU code format
   * Allows only letters, numbers, hyphens, and underscores (max 32 characters)
   */
  private skuCodeValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (!value) {
      return null; // Let required validator handle null/undefined values
    }

    const trimmedValue = value.trim();

    // Spaces-only values are invalid
    if (trimmedValue === '') {
      return { spacesOnly: true };
    }

    // Check length limit (32 characters maximum)
    if (trimmedValue.length > 32) {
      return {
        maxLength: { requiredLength: 32, actualLength: trimmedValue.length },
      };
    }

    // Regular expression to allow only letters, numbers, hyphens, and underscores
    const validSkuCodePattern = /^[a-zA-Z0-9_-]+$/;
    if (!validSkuCodePattern.test(trimmedValue)) {
      return { invalidFormat: true };
    }

    return null;
  }

  /**
   * Check if the SKU item has any image data to display
   */
  hasImageData(item: SKUItem): boolean {
    return this.hasMainImages(item) || this.hasDetailImages(item);
  }

  /**
   * Check if main images exist and are valid
   */
  hasMainImages(item: SKUItem): boolean {
    return !!(
      item.mainImages &&
      Array.isArray(item.mainImages) &&
      item.mainImages.length > 0
    );
  }

  /**
   * Check if detail images exist and are valid
   */
  hasDetailImages(item: SKUItem): boolean {
    return !!(
      item.detailImages &&
      Array.isArray(item.detailImages) &&
      item.detailImages.length > 0
    );
  }

  // Barcode management methods
  onAddBarcode(): void {
    this.editingBarcode.set(null);
    this.showBarcodeDialog.set(true);
  }

  onEditBarcode(barcode: Barcode): void {
    this.editingBarcode.set({ ...barcode });
    this.showBarcodeDialog.set(true);
  }

  onDeleteBarcode(barcode: Barcode): void {
    this.confirmationService.confirm({
      key: 'skuConfirm',
      header: this.translateService.instant(
        'sku_management.confirmations.delete_title',
      ),
      message: this.translateService.instant(
        'sku_management.confirmations.delete_single',
        {
          skuName: barcode.barcodeValue,
        },
      ),
      acceptLabel: this.translateService.instant(
        'sku_management.confirmations.delete',
      ),
      rejectLabel: this.translateService.instant(
        'sku_management.confirmations.cancel',
      ),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => {
        this.deleteBarcode(barcode);
      },
    });
  }

  private deleteBarcode(barcode: Barcode): void {
    this.barcodeList.update((list) => list.filter((b) => b.id !== barcode.id));
  }

  onDeleteSelectedBarcodes(): void {
    const selected = this.selectedBarcodes();
    if (selected.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'sku_management.barcode.messages.no_barcodes_selected',
        ),
      });
      return;
    }

    this.confirmationService.confirm({
      key: 'skuConfirm',
      header: this.translateService.instant(
        'sku_management.confirmations.delete_title',
      ),
      message: this.translateService.instant(
        'sku_management.confirmations.delete_multiple',
        {
          count: selected.length,
        },
      ),
      acceptLabel: this.translateService.instant(
        'sku_management.confirmations.delete',
      ),
      rejectLabel: this.translateService.instant(
        'sku_management.confirmations.cancel',
      ),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => {
        this.deleteSelectedBarcodes(selected);
      },
    });
  }

  private deleteSelectedBarcodes(selected: Barcode[]): void {
    const selectedIds = new Set(selected.map((b) => b.id));
    this.barcodeList.update((list) =>
      list.filter((b) => !selectedIds.has(b.id)),
    );
    this.selectedBarcodes.set([]);
  }

  onPackageComponentQuantityChange(component: PackageSkuComponent): void {
    const quantity = Math.max(1, Number(component.num || 1));
    this.packageComponents.update((components) =>
      components.map((item) =>
        item.packageSkuId === component.packageSkuId
          ? {
              ...item,
              num: quantity,
            }
          : item,
      ),
    );
  }

  isPackageComponentSelected(component: PackageSkuComponent): boolean {
    return this.selectedPackageComponents().some(
      (item) => item.packageSkuId === component.packageSkuId,
    );
  }

  isAllPackageComponentsSelected(): boolean {
    const components = this.packageComponents();
    return (
      components.length > 0 &&
      this.selectedPackageComponents().length === components.length
    );
  }

  onPackageComponentSelectAll(event: { checked?: boolean }): void {
    this.selectedPackageComponents.set(
      event.checked ? [...this.packageComponents()] : [],
    );
  }

  onPackageComponentSelectionChange(
    event: { checked?: boolean },
    component: PackageSkuComponent,
  ): void {
    this.selectedPackageComponents.update((selected) =>
      event.checked
        ? [
            ...selected.filter(
              (item) => item.packageSkuId !== component.packageSkuId,
            ),
            component,
          ]
        : selected.filter(
            (item) => item.packageSkuId !== component.packageSkuId,
          ),
    );
  }

  removePackageComponent(component: PackageSkuComponent): void {
    this.confirmationService.confirm({
      key: 'skuConfirm',
      header: this.translateService.instant(
        'sku_management.confirmations.delete_title',
      ),
      message: this.translateService.instant(
        'sku_management.confirmations.delete_single',
        {
          skuName: component.skuName || component.skuCode,
        },
      ),
      acceptLabel: this.translateService.instant(
        'sku_management.confirmations.delete',
      ),
      rejectLabel: this.translateService.instant(
        'sku_management.confirmations.cancel',
      ),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => {
        this.deletePackageComponent(component);
      },
    });
  }

  removeSelectedPackageComponents(): void {
    const selected = this.selectedPackageComponents();
    if (selected.length === 0) {
      return;
    }

    this.confirmationService.confirm({
      key: 'skuConfirm',
      header: this.translateService.instant(
        'sku_management.confirmations.delete_title',
      ),
      message: this.translateService.instant(
        'sku_management.confirmations.delete_multiple',
        { count: selected.length },
      ),
      acceptLabel: this.translateService.instant(
        'sku_management.confirmations.delete',
      ),
      rejectLabel: this.translateService.instant(
        'sku_management.confirmations.cancel',
      ),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => this.deleteSelectedPackageComponents(),
    });
  }

  private deletePackageComponent(component: PackageSkuComponent): void {
    this.packageComponents.update((components) =>
      components.filter((item) => item.packageSkuId !== component.packageSkuId),
    );
    this.selectedPackageComponents.update((selected) =>
      selected.filter((item) => item.packageSkuId !== component.packageSkuId),
    );
  }

  private deleteSelectedPackageComponents(): void {
    const selectedIds = new Set(
      this.selectedPackageComponents().map(
        (component) => component.packageSkuId,
      ),
    );
    this.packageComponents.update((components) =>
      components.filter(
        (component) => !selectedIds.has(component.packageSkuId),
      ),
    );
    this.selectedPackageComponents.set([]);
  }

  onBarcodeDialogConfirm(barcodeData: Barcode): void {
    if (this.editingBarcode()) {
      // Update existing barcode
      this.barcodeList.update((list) =>
        list.map((b) => (b.id === barcodeData.id ? barcodeData : b)),
      );
    } else {
      // Add new barcode
      const newBarcode: Barcode = {
        ...barcodeData,
        id: this.nextBarcodeId(),
      };
      this.barcodeList.update((list) => [...list, newBarcode]);
      this.nextBarcodeId.update((id) => id + 1);
    }
    this.showBarcodeDialog.set(false);
    this.editingBarcode.set(null);
  }

  onBarcodeSelectionChange(selected: Barcode[]): void {
    this.selectedBarcodes.set(selected);
  }

  isAllBarcodesSelected(): boolean {
    const list = this.barcodeList();
    return list.length > 0 && this.selectedBarcodes().length === list.length;
  }

  onSelectAllBarcodes(event: any): void {
    if (event.checked) {
      this.selectedBarcodes.set([...this.barcodeList()]);
    } else {
      this.selectedBarcodes.set([]);
    }
  }

  isBarcodeRowSelected(barcode: Barcode): boolean {
    return this.selectedBarcodes().some((b) => b.id === barcode.id);
  }

  onBarcodeRowSelectionChange(event: any, barcode: Barcode): void {
    if (event.checked) {
      this.selectedBarcodes.update((list) => [...list, barcode]);
    } else {
      this.selectedBarcodes.update((list) =>
        list.filter((b) => b.id !== barcode.id),
      );
    }
  }
}
